// Wave 5 Phase 2 — client-side 402 quota handling wrapper.
// Wave 5 Phase 3b — deferred-promise retry mechanism.
//
// Thin wrapper around supabase.functions.invoke for the 6 gated generators.
// Detects HTTP 402 (server-side quota block from Wave 5 Phase 1) and
// dispatches a window CustomEvent so a top-level listener can navigate to
// the paywall route. All other outcomes (2xx success, non-402 errors) are
// passed through byte-identically to the raw invoke call.
//
// While PAYWALL_UI_ENABLED is false, a real 402 (defensive — none should
// exist in prod since PAYWALL_ENFORCEMENT_ENABLED is also false) is logged
// + toasted instead of dispatched. Double-gate safety.
//
// Dev override: ?force_paywall=1 in the URL simulates a 402 branch on the
// next invoke call, letting Ola preview the paywall route without server
// involvement.
//
// Phase 3b: when the paywall UI is engaged, the wrapper stashes the caller's
// {name, body, source} into a single-slot module-level pendingPaywallCall
// and returns a promise that awaits paywall outcome. resolvePaywallCall(true)
// re-invokes the function with the original args; resolvePaywallCall(false)
// resolves with the original quotaExceeded shape (dismiss). Single-slot
// invariant: only one modal paywall at a time (last-write-wins; older
// pending entries are silently resolved with quotaExceeded before overwrite).

import { supabase } from "@/integrations/supabase/client";
import { PAYWALL_UI_ENABLED } from "@/lib/featureFlags";
import { toast } from "sonner";

export type GeneratorName =
  | "analyse-outfit"
  | "analyse-outfit-url"
  | "analyse-outfit-social"
  | "analyse-outfit-text"
  | "style-my-item"
  | "swap-pairing-item";

export type PaywallEventDetail = {
  generator: GeneratorName | "unknown";
  source: string;
  outfit_count: number;
  quota: number;
};

export type InvokeResult<T = unknown> = {
  data: T | null;
  // Same FunctionsError-ish shape supabase.functions.invoke returns.
  error: { message?: string; [k: string]: unknown } | null;
  quotaExceeded: boolean;
};

const PAYWALL_EVENT = "l2f:paywall-required";
const PENDING_TTL_MS = 10 * 60 * 1000; // 10 min defensive TTL.

type PendingCall = {
  name: GeneratorName;
  body: unknown;
  source: string;
  resolve: (r: InvokeResult) => void;
  timestamp: number;
};

// Single-slot pending call. Only one modal paywall at a time per spec (D3).
let pendingPaywallCall: PendingCall | null = null;

function quotaExceededResult<T = unknown>(): InvokeResult<T> {
  return { data: null, error: null, quotaExceeded: true };
}

function stashPending(entry: Omit<PendingCall, "timestamp">): void {
  // If there's an old pending entry (either stale or displaced by a new
  // paywall), silently resolve it with quotaExceeded before overwriting so
  // no promise is orphaned.
  if (pendingPaywallCall) {
    try {
      pendingPaywallCall.resolve(quotaExceededResult());
    } catch {
      /* defensive */
    }
    pendingPaywallCall = null;
  }
  pendingPaywallCall = { ...entry, timestamp: Date.now() };
}

/**
 * Called by the paywall UI after purchase/restore/dismiss.
 * success=true → re-invoke original function with same args, resolve caller
 *                with the fresh result.
 * success=false → resolve caller with the original quotaExceeded shape (caller
 *                 already handled it once; this is the dismiss path).
 *
 * Always resolves — never rejects. Cleanup is unconditional.
 */
export async function resolvePaywallCall(success: boolean): Promise<void> {
  const entry = pendingPaywallCall;
  pendingPaywallCall = null;
  if (!entry) return;

  // Defensive TTL check: if the pending entry is ancient, silently drop.
  if (Date.now() - entry.timestamp > PENDING_TTL_MS) {
    entry.resolve(quotaExceededResult());
    return;
  }

  if (!success) {
    entry.resolve(quotaExceededResult());
    return;
  }

  // Retry the original invoke. Report success/failure via analytics so we
  // can measure retry conversion.
  try {
    const { data, error } = await supabase.functions.invoke(entry.name, {
      body: entry.body as Record<string, unknown> | undefined,
    });
    try {
      window.dispatchEvent(
        new CustomEvent("l2f:paywall-generator-resumed", {
          detail: { generator: entry.name, success: !error },
        }),
      );
    } catch {
      /* no-op */
    }
    entry.resolve({
      data: (data ?? null) as unknown,
      error: (error ?? null) as InvokeResult["error"],
      quotaExceeded: false,
    });
  } catch (err) {
    entry.resolve({
      data: null,
      error: { message: (err as Error)?.message ?? "retry_failed" },
      quotaExceeded: false,
    });
  }
}

/** Test helper — clears pending without resolving. Not used in prod. */
export function __clearPendingPaywallCall(): void {
  pendingPaywallCall = null;
}

function isForcePaywall(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("force_paywall") === "1";
  } catch {
    return false;
  }
}

function dispatchPaywall(detail: PaywallEventDetail): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(PAYWALL_EVENT, { detail }));
}

async function readErrorBody(err: unknown): Promise<{ status: number | null; body: Record<string, unknown> | null }> {
  const ctx = (err as { context?: { response?: Response } } | null)?.context;
  const response = ctx?.response;
  if (!response) return { status: null, body: null };
  const status = response.status ?? null;
  let body: Record<string, unknown> | null = null;
  try {
    body = (await response.clone().json()) as Record<string, unknown>;
  } catch {
    body = null;
  }
  return { status, body };
}

export async function invokeGenerator<T = unknown>(
  name: GeneratorName,
  options: { body?: unknown; source?: string } = {},
): Promise<InvokeResult<T>> {
  const source = options.source ?? name;

  // Dev override: simulate 402 branch pre-invoke (no server call).
  if (isForcePaywall()) {
    const detail: PaywallEventDetail = {
      generator: name,
      source,
      outfit_count: 5,
      quota: 5,
    };
    if (PAYWALL_UI_ENABLED) {
      // Stash + return a deferred promise so dev-mode purchase completion
      // still triggers the retry mechanism.
      return new Promise<InvokeResult<T>>((resolve) => {
        stashPending({
          name,
          body: options.body,
          source,
          resolve: resolve as (r: InvokeResult) => void,
        });
        dispatchPaywall(detail);
      });
    }
    console.info("[invokeGenerator] force_paywall (flag OFF, no dispatch)", detail);
    toast("Monthly limit reached (placeholder)");
    return quotaExceededResult<T>();
  }

  const { data, error } = await supabase.functions.invoke<T>(name, {
    body: options.body as Record<string, unknown> | undefined,
  });

  if (error) {
    const { status, body } = await readErrorBody(error);
    const isQuota =
      status === 402 ||
      (body && (body as { error?: string }).error === "quota_exceeded");
    if (isQuota) {
      const detail: PaywallEventDetail = {
        generator: name,
        source,
        outfit_count: Number((body as { outfit_count?: number } | null)?.outfit_count ?? 0),
        quota: Number((body as { quota?: number } | null)?.quota ?? 5),
      };
      if (PAYWALL_UI_ENABLED) {
        return new Promise<InvokeResult<T>>((resolve) => {
          stashPending({
            name,
            body: options.body,
            source,
            resolve: resolve as (r: InvokeResult) => void,
          });
          dispatchPaywall(detail);
        });
      }
      console.info("[invokeGenerator] 402 caught (flag OFF, no dispatch)", detail);
      toast("Monthly limit reached (placeholder)");
      return quotaExceededResult<T>();
    }
  }

  return {
    data: (data ?? null) as T | null,
    error: (error ?? null) as InvokeResult<T>["error"],
    quotaExceeded: false,
  };
}
